import { NgClass, NgOptimizedImage } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import {
  MatError,
  MatFormField,
  MatOption,
  MatSelect,
} from '@angular/material/select';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { API_PARAMS } from '../../shared/constants/api.constants';
import { ID_LOGIN_TYPE } from '../../shared/constants/common.constants';
import { CITIES } from '../../shared/constants/location.constants';
import {
  AppointmentInfoField,
  BookDatetimeInfo,
  BookingInfo,
  BusinessCheckupSettingsRequest,
  District,
  PersonalCheckupSettingsRequest,
} from '../../shared/interfaces/appointment-info.model';
import { AddExamInfo } from '../../shared/interfaces/reserve-add.model';
import { ExamItemName } from '../../shared/interfaces/reserve-confirm-survey.model';
import {
  BusinessExamInfo,
  BusinessPackageInfo,
  Group,
} from '../../shared/interfaces/reserve-loading.model';
import { AppointmentInfoService } from '../../shared/services/appointment-info.service';
import { DevService } from '../../shared/services/dev.service';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { emailValidator } from '../../shared/utils/validator.util';

/**
 * 檢查相關資料組件
 */
@Component({
  selector: 'app-appointment-info',
  standalone: true,
  imports: [
    TranslatePipe,
    MatSelect,
    MatOption,
    ReactiveFormsModule,
    MatInput,
    MatRadioGroup,
    MatRadioButton,
    MatCheckbox,
    MatError,
    MatFormField,
    NgClass,
    NgOptimizedImage,
    MatButton,
    MatButtonToggleGroup,
    MatButtonToggle,
    FormsModule,
  ],
  templateUrl: './appointment-info.component.html',
  styleUrl: './appointment-info.component.scss',
})
export class AppointmentInfoComponent implements OnInit {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  translateService: TranslateService = inject(TranslateService);
  appointmentInfoService: AppointmentInfoService = inject(
    AppointmentInfoService
  );
  devService: DevService = inject(DevService);
  router: Router = inject(Router);

  form = new FormGroup({
    city: new FormControl('', [Validators.required]), // 檢前資料縣市
    district: new FormControl<{
      areaEngName: string;
      areaName: string;
      zipCode: string;
    } | null>(null, [Validators.required]), // 檢前資料區域
    address: new FormControl('', [Validators.required,Validators.maxLength(173)]), // 檢前資料地址
    deliveryMethod: new FormControl(API_PARAMS.DELIVERY_METHOD.DOMESTIC.code, [
      Validators.required,
    ]), // 領取方式
    reportCity: new FormControl('', [Validators.required]), // (郵寄國內)檢查報告寄送縣市
    reportDistrict: new FormControl<{
      areaEngName: string;
      areaName: string;
      zipCode: string;
    } | null>(null, [Validators.required]), // (郵寄國內)檢查報告寄送區域
    reportAddress: new FormControl('', [Validators.required,Validators.maxLength(173)]), // (郵寄國內)檢查報告寄送地址
    deliveryRecipient: new FormControl('', [Validators.required]), // 收件人資料
    recipient: new FormControl(''), // (非本人)報告收件人姓名
    recipientCell: new FormControl(''), // (非本人)報告收件人手機號碼
    sameAsPreExamInfoChecked: new FormControl<boolean>(false),
    foreignAddress: new FormControl('',[Validators.maxLength(180)]), // (郵寄國外)寄送地址
    email: new FormControl(''), // (電子檔)信箱
    language: new FormControl('', [Validators.required]), // 檢查報告語言
    clothSize: new FormControl('', [Validators.required]), // 健檢服尺寸
    breakfast: new FormControl('', [Validators.required]), // 早餐
    lunch: new FormControl(''), // 午餐
  });

  phoneValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    if (value) {
      const phoneRegex = /^\d{10}$/; // 手機號碼為10位數字
      if (!phoneRegex.test(value)) {
        return {
          invalidFormat: this.translateService.instant(
            'error.phone-error.format'
          ),
        };
      }
    }
    return null;
  }
  @ViewChildren('fieldRef') fieldRefs!: QueryList<ElementRef>;

  languages: Array<AppointmentInfoField> = []; // 檢查報告語言
  breakfasts: Array<AppointmentInfoField> = []; // 早餐選項
  lunches: Array<AppointmentInfoField> = []; // 午餐選項
  clothesSizes: Array<AppointmentInfoField> = []; // 健檢服尺寸

  noticeSendType: string = ''; // 檢前資料寄送方式（代碼）1：個別寄送 2：承辦領取 3：不須寄送
  reportSendType: string = '1'; // 報告領取方式（代碼）1：個別寄送 2：承辦領取 3：不須寄送 99：其他(不需寄送)

  showFormError: boolean = false; // 是否顯示表單錯誤
  isChecked:boolean= false; // 是否勾選同檢前資料

  protected readonly cities = CITIES.map(
    (city: { cityName: string }) => city.cityName
  ); //縣市下拉選單
  protected districts: {
    areaName: string;
    zipCode: string;
    areaEngName: string;
  }[] = []; //檢前資料地址-行政區下拉選單
  protected reportDistricts: {
    areaName: string;
    zipCode: string;
    areaEngName: string;
  }[] = []; // 檢查報告地址-行政區下拉選單
  selectedDistrict: {
    areaName: string;
    zipCode: string;
    areaEngName: string;
  } | null = null; // 檢前資料地址-已選擇的行政區
  selectedReportDistrict: {
    areaName: string;
    zipCode: string;
    areaEngName: string;
  } | null = null; // 檢查報告地址-已選擇的行政區
  protected readonly API_PARAMS = API_PARAMS;

  ngOnInit(): void {
    // 初始化表單數據
    const bookingInfo = this.sharedStorageService.get('bookingInfo');
    //初始化判斷是否同檢前資料
    this.isChecked= bookingInfo?.isChecked ?? false;
    console.log('this.isChecked:', this.isChecked);
    // 初始化領取方式
    console.log("bookingInfo?.deliveryMethod",bookingInfo?.deliveryMethod);
    this.form.get('deliveryMethod')?.setValue(bookingInfo?.deliveryMethod ?? API_PARAMS.DELIVERY_METHOD.DOMESTIC.code);
    // 收件人資料(預設本人)
    this.form.get('deliveryRecipient')?.setValue(API_PARAMS.DELIVERY_METHOD.SELF.code);
    // 檢前資料寄送地址
    // city 選擇變更時
    console.log('cities:', this.cities);
    this.form.get('city')?.valueChanges.subscribe((selectedCityName: string | null) => {
      if (!selectedCityName) {
        this.districts = [];
        this.form.get('district')?.setValue(null);
        this.selectedDistrict = null;
        return;
      }
      console.log('檢前地址city 選擇變更', selectedCityName);
      const city = CITIES.find(c => c.cityName === selectedCityName);
      this.districts = city ? city.areaList : [];
      this.form.get('district')?.setValue(null);
      this.selectedDistrict = null;
    });

    // district 選擇變更時
    this.form.get('district')?.valueChanges.subscribe((selectedDistrictName) => {
      if (!selectedDistrictName) {
        this.selectedDistrict = null;
        return;
      }

      const district = this.districts.find(
        a => a.areaName === selectedDistrictName.areaName
      );
      this.selectedDistrict = district || null;
    });

    // 檢查報告寄送地址
    // reportCity 選擇變更時
    this.form.get('reportCity')?.valueChanges.subscribe((selectedCityName: string | null) => {
      if (!selectedCityName) {
        this.reportDistricts = [];
        this.form.get('reportDistrict')?.setValue(null);
        this.selectedReportDistrict = null;
        return;
      }
      console.log('報告地址city 選擇變更', selectedCityName);
      const city = CITIES.find(c => c.cityName === selectedCityName);
      this.reportDistricts = city ? city.areaList : [];
      this.form.get('reportDistrict')?.setValue(null);
      this.selectedReportDistrict = null;

      // ✅ 檢查是否與檢前資料相同
      this.checkAndUpdateIsChecked();
    });

    // reportDistrict 選擇變更時
    this.form.get('reportDistrict')?.valueChanges.subscribe((selectedDistrictName) => {
      if (!selectedDistrictName) {
        this.selectedReportDistrict = null;
        return;
      }

      const district = this.reportDistricts.find(
        a => a.areaName === selectedDistrictName.areaName
      );
      this.selectedReportDistrict = district || null;

      // ✅ 檢查是否與檢前資料相同
      this.checkAndUpdateIsChecked();
    });

    // ✅ reportAddress 選擇變更時
    this.form.get('reportAddress')?.valueChanges.subscribe((reportAddress) => {
      console.log('reportAddress 變更:', reportAddress);
      this.checkAndUpdateIsChecked();
    });

    // 設置當前預約步驟為6
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 6);
    });

    if (this.isPersonalReserve) {
      // 查詢檢查相關資料欄位

      const sharedStorageExamItemList =
        this.sharedStorageService.get('examItemList') ?? [];
      const newExamItemList = sharedStorageExamItemList.map(item => ({
        examItem: item.examItem,
        examName: item.examName,
        selectFlag: item.selectFlag ?? 1,
      }))?? [];; //轉換成API接受的request格式

      // 取得加選檢項
      const addList: Array<ExamItemName> =
        this.sharedStorageService
          .get('addExamItemList')
          ?.map((addExamInfo: AddExamInfo) => {
            return {
              examItem: addExamInfo.examItem,
              examName: addExamInfo.examName,
            };
          }) ?? [];

      const request: PersonalCheckupSettingsRequest = {
        dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.ALL,
        caseNo: this.sharedStorageService.get('myReservation')?.caseNo ?? this.sharedStorageService.get('caseNo') ??'',
        companyNo: '1150',
        orgNo: this.sharedStorageService.get('userPlanRequest')?.orgNo ?? '',
        gender: this.sharedStorageService.get('userPlanRequest')?.gender ?? '',
        idNo: this.sharedStorageService.get('userPlanRequest')?.idNo ?? '',
        UUID: this.sharedStorageService.get('userPlanRequest')?.uuid ?? '',
        bookDate:this.sharedStorageService.get('myReservation')?.bookDate ?? '',
        examItemList: newExamItemList ,
        addList: addList,
      };
      console.log('查詢檢查request:', request);
      this.appointmentInfoService.checkupSettings(request).subscribe(res => {
        this.languages = (res.data?.languInfos ?? []).sort(
          (a, b) => Number(a.uno) - Number(b.uno)
        );
        this.clothesSizes = (res.data?.sizeInfos ?? []).sort(
          (a, b) => Number(a.uno) - Number(b.uno)
        );
        //語言預設第一個
        this.form.get('language')?.setValue(this.languages[0].uno);

        this.breakfasts = res.data?.breakfastInfos ?? [];
        this.lunches = res.data?.lunchInfos ?? [];
        // 當有午餐選項時，設定午餐為必填
          if (this.lunches.length > 0) {
            this.form.get('lunch')?.setValidators([Validators.required]);
            this.form.get('lunch')?.updateValueAndValidity();
          }
      });

      
    } else {
      //企業預約
      
      // 取得企業已選檢項
      const examItemList =
        this.sharedStorageService.get('examItemList') ?? [];
      const newExamItemList = examItemList.map(item => ({
        examItem: item.examItem,
        examName: item.examName
      }));

      // 取得加選檢項
      const addList: Array<ExamItemName> =
        this.sharedStorageService
          .get('addExamItemList')
          ?.map((addExamInfo: AddExamInfo) => {
            return {
              examItem: addExamInfo.examItem,
              examName: addExamInfo.examName,
            };
          }) ?? [];

      const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
      const idLoginRequest = this.sharedStorageService.get('idLoginRequest');//走漸進式登入
      const businessLoginInfo = this.sharedStorageService.get('businessLoginInfo');//走會員登入
      const createMemberRequest
        = this.sharedStorageService.get('createMemberRequest');//走會員登入
      // 查詢檢查相關資料欄位CB-074
      const request: BusinessCheckupSettingsRequest = {
        dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.ALL,
        caseNo: this.sharedStorageService.get('myReservation')?.caseNo ?? this.sharedStorageService.get('caseNo') ??'',
        companyNo: businessPlanRequest?.companyNo ?? '',
        familyFlag: idLoginRequest?.identity ?? businessLoginInfo?.identity ?? this.sharedStorageService.get('myReservation')?.familyFlag ?? '',
        orgNo: businessPlanRequest?.orgNo ?? '',
        gender: businessPlanRequest?.gender ?? '',
        idNo: idLoginRequest?.idNo ?? createMemberRequest?.idNo ?? '',
        bookDate:this.sharedStorageService.get('myReservation')?.bookDate ?? '',
        examItemList: newExamItemList,
        addList: addList,
      };
      console.log('企業檢查request:', request);
      this.appointmentInfoService
        .businessCheckupSettings(request)
        .subscribe(res => {
          this.languages = res.data?.languInfos ?? [];
          this.clothesSizes = res.data?.sizeInfos ?? [];
          this.breakfasts = res.data?.breakfastInfos ?? [];
          this.lunches = res.data?.lunchInfos ?? [];
          //語言預設第一個
          this.languages = (res.data?.languInfos ?? []).sort(
            (a, b) => Number(a.uno) - Number(b.uno)
          );
          this.clothesSizes = (res.data?.sizeInfos ?? []).sort(
            (a, b) => Number(a.uno) - Number(b.uno)
          );
          this.form.get('language')?.setValue(this.languages[0].uno);
          // 當有午餐選項時，設定午餐為必填
          if (this.lunches.length > 0) {
            this.form.get('lunch')?.setValidators([Validators.required]);
            this.form.get('lunch')?.updateValueAndValidity();
          }
          this.noticeSendType =
            res.data?.noticeSendType ?? '1';
          this.reportSendType =
            res.data?.reportSendType ?? '1';

            //測試其他情境
            // this.noticeSendType ='3';
            // this.reportSendType = '99';
            if(this.reportSendType === API_PARAMS.DELIVERY_METHOD.INDIVIDUAL.code ||this.reportSendType ===API_PARAMS.DELIVERY_METHOD.DOMESTIC.code){
              //如果是個別寄送，且沒有儲存過deliveryMethod，則預設為郵寄國內
              if(!bookingInfo?.deliveryMethod){
                this.form.get('deliveryMethod')?.setValue(API_PARAMS.DELIVERY_METHOD.DOMESTIC.code);
              }
            }else{
              //清除檢核
              console.log('檢後報告地址-清除檢核');
              this.form.get('deliveryMethod')?.clearValidators();
              this.form.get('reportCity')?.clearValidators();
              this.form.get('reportDistrict')?.clearValidators();
              this.form.get('reportAddress')?.clearValidators();
              this.form.get('foreignAddress')?.clearValidators();
              this.form.get('email')?.clearValidators();
              this.form.get('deliveryRecipient')?.clearValidators();
              this.form.get('recipient')?.clearValidators();
              this.form.get('recipientCell')?.clearValidators();

              this.form.get('deliveryMethod')?.updateValueAndValidity({ emitEvent: false });
              this.form.get('reportCity')?.updateValueAndValidity({ emitEvent: false });
              this.form.get('reportDistrict')?.updateValueAndValidity({ emitEvent: false });
              this.form.get('reportAddress')?.updateValueAndValidity({ emitEvent: false });
              this.form.get('foreignAddress')?.updateValueAndValidity({ emitEvent: false });
              this.form.get('email')?.updateValueAndValidity({ emitEvent: false });
              this.form.get('deliveryRecipient')?.updateValueAndValidity({ emitEvent: false });
              this.form.get('recipient')?.updateValueAndValidity({ emitEvent: false });
              this.form.get('recipientCell')?.updateValueAndValidity({ emitEvent: false });
            }
        });

    }

    // 監聽deliveryMethod變化時，添加/刪除相關欄位校驗
      this.form.get('deliveryMethod')?.valueChanges.subscribe(value => {
            if (value === API_PARAMS.DELIVERY_METHOD.DOMESTIC.code) {
              //寄國內
              console.log("寄國內");
              this.form.get('reportCity')?.setValidators([Validators.required]);
              this.form.get('reportDistrict')?.setValidators([Validators.required]);
              this.form.get('reportAddress')?.setValidators([Validators.required,Validators.maxLength(173)]);
              this.form.get('foreignAddress')?.clearValidators();
              this.form.get('foreignAddress')?.setValue(null); 
              this.form.get('email')?.clearValidators();
            } else if (value === API_PARAMS.DELIVERY_METHOD.FOREIGN.code) {
              console.log("寄國外");
              this.form.get('foreignAddress')?.setValidators([Validators.required,Validators.maxLength(180)]);
              this.form.get('reportCity')?.clearValidators();
              this.form.get('reportDistrict')?.clearValidators();
              this.form.get('reportAddress')?.clearValidators();
              this.form.get('email')?.clearValidators();
              this.form.get('reportCity')?.setValue(null); 
              this.form.get('reportDistrict')?.setValue(null); 
              this.form.get('reportAddress')?.setValue(null); 
              this.form.get('email')?.setValue(null); 
              this.toggleSameAsPreExamInfo(false);
              this.isChecked = false;
            }
            this.form.get('reportCity')?.updateValueAndValidity();
            this.form.get('reportDistrict')?.updateValueAndValidity();
            this.form.get('reportAddress')?.updateValueAndValidity();
            this.form.get('foreignAddress')?.updateValueAndValidity();
            this.form.get('email')?.updateValueAndValidity();
          });

    //監聽收件人資料變化時，添加/刪除相關欄位校驗
    this.form.get('deliveryRecipient')?.valueChanges.subscribe(value => {
      if (value === API_PARAMS.DELIVERY_METHOD.NOT_SELF.code) {
        this.form.get('recipient')?.setValidators([Validators.required]);
        this.form
          .get('recipientCell')
          ?.setValidators([
            Validators.required,
            (control: FormControl) => this.phoneValidator(control),
          ]);
      } else {
        this.form.get('recipient')?.clearValidators();
        this.form.get('recipientCell')?.clearValidators();
      }
      this.form.get('recipient')?.updateValueAndValidity();
      this.form.get('recipientCell')?.updateValueAndValidity();
    });

    
    if (bookingInfo) {
      // 設定 city
      this.form
        .get('city')
        ?.setValue(bookingInfo.city ?? '', { emitEvent: false });

      // 取得該 city 對應的 district 陣列(初始化下拉選單)
      this.districts =
        CITIES.find(c => c.cityName === bookingInfo.city)?.areaList || [];
      // 用 areaName找到對應的 District 物件(因zipCode非唯一值，例:新竹市行政區都是300)
      const initDistrict =
        this.districts.find(d => d.areaName === bookingInfo?.district) ?? null;
      // 設定到 FormControl（這樣 mat-select 才會選中）
      this.form.get('district')?.setValue(initDistrict, { emitEvent: false });
      console.log('初始化district', this.form.get('district')?.value);

      this.form.get('address')?.setValue(bookingInfo.address ?? '');
      this.form
        .get('deliveryMethod')
        ?.setValue(
          bookingInfo.deliveryMethod ?? API_PARAMS.DELIVERY_METHOD.DOMESTIC.code
        );

      this.form
        .get('reportCity')
        ?.setValue(bookingInfo.reportCity ?? '', { emitEvent: false });
      // 取得該 city 對應的 district 陣列(初始化下拉選單)
      this.reportDistricts =
        CITIES.find(c => c.cityName === bookingInfo.reportCity)?.areaList || [];
      // 用 zipCode 找到對應的 District 物件
      const initReportDistrict =
        this.reportDistricts.find(
          d => d.areaName === bookingInfo?.reportDistrict
        ) ?? null;
      // 設定到 FormControl（這樣 mat-select 才會選中）
      this.form
        .get('reportDistrict')
        ?.setValue(initReportDistrict, { emitEvent: false });
      console.log(
        '初始化reportDistrict',
        this.form.get('reportDistrict')?.value
      );

      this.form.get('reportAddress')?.setValue(bookingInfo.reportAddress ?? '');
      this.form
        .get('foreignAddress')
        ?.setValue(bookingInfo.foreignAddress ?? '');
      this.form
        .get('deliveryRecipient')
        ?.setValue( bookingInfo.deliveryRecipient ?? API_PARAMS.DELIVERY_METHOD.SELF.code);
        
      this.form.get('recipient')?.setValue(bookingInfo.recipient ?? '');
      this.form.get('recipientCell')?.setValue(bookingInfo.recipientCell ?? '');
      this.form.get('email')?.setValue(bookingInfo.email ?? '');
      if (bookingInfo.clothesSize) {
        this.form.get('clothSize')?.setValue(bookingInfo.clothesSize ?? '');
      } else {
        this.form.get('clothSize')?.setValue(this.clothesSizes[0]?.uno ?? ''); // 預設選擇第一個尺寸
      }
      if (bookingInfo.language) {
        this.form.get('language')?.setValue(bookingInfo.language ?? '');
      } else {
        this.form.get('language')?.setValue(this.languages[0]?.uno ?? ''); // 預設選擇第一個語言
      }
      this.form.get('breakfast')?.setValue(bookingInfo.breakfast ?? '');
      this.form.get('lunch')?.setValue(bookingInfo.lunch ?? '');
    }
  }

  private originalReportCity: string | null = null;
  private originalReportDistrict: District | null = null;
  private originalReportAddress: string | null = null;

  toggleSameAsPreExamInfo(checked: boolean) {
    if (checked) {
      this.isChecked= true; // 是否勾選同檢前資料
      // 勾選前先存原始值
      this.originalReportCity = this.form.get('reportCity')?.value ?? '';
      this.originalReportDistrict =
        this.form.get('reportDistrict')?.value ?? null;
      this.originalReportAddress = this.form.get('reportAddress')?.value ?? '';

      // 用檢前資料覆蓋
      this.form.get('reportCity')?.setValue(this.form.get('city')?.value ?? '');
      this.form
        .get('reportDistrict')
        ?.setValue(this.form.get('district')?.value ?? null);
      this.form
        .get('reportAddress')
        ?.setValue(this.form.get('address')?.value ?? '');

      //收件人資料強制勾"本人"，並且禁用
      this.form.get('deliveryRecipient')?.setValue(API_PARAMS.DELIVERY_METHOD.SELF.code ?? '01');
      this.form.get('deliveryRecipient')?.disable();
      
    } else {
      // 取消勾選 → 還原成原本值
      this.isChecked= false;// 是否勾選同檢前資料
      this.form.get('reportCity')?.setValue(this.originalReportCity ?? '');
      this.form
        .get('reportDistrict')
        ?.setValue(this.originalReportDistrict ?? null);
      this.form
        .get('reportAddress')
        ?.setValue(this.originalReportAddress ?? '');
        //還原收件人資料變可選
      this.form.get('deliveryRecipient')?.enable();
      
    }
    this.selectedReportDistrict =
      this.form.get('reportDistrict')?.value ?? null;

      

  }

  onInputFocus() {
    const checkboxCtrl = this.form.get('sameAsPreExamInfoChecked');
    if (checkboxCtrl?.value) {
      checkboxCtrl.setValue(false);
      this.toggleSameAsPreExamInfo(false);
    }
  }

  // 取得檢前資料地址錯誤訊息
  getPreExamAddressErrorMessage(): string {
    if (!this.showFormError) return '';
    const errors: string[] = [];
    if (this.form.get('city')?.errors)
      errors.push(this.translateService.instant('appointment-info.city'));
    if (this.form.get('district')?.errors)
      errors.push(this.translateService.instant('appointment-info.district'));

    const addressErrors = this.form.get('address')?.errors;
    if (addressErrors) {
      if (addressErrors['required']) {
        errors.push(this.translateService.instant('appointment-info.detailed-address'));
      }
      if (addressErrors['maxlength']) {
        // 超過字數限制 → 直接回傳，不要再跟其他欄位組合
        return this.translateService.instant('appointment-info.detailed-address-maxlength');
      }
    }
    return errors.length
      ? this.translateService.instant('error.please-fill-in', {
          fieldName: errors.join('、'),
        })
      : '';
  }

  // 取得國內檢查報告地址錯誤訊息
  getReportAddressErrorMessage(): string {
    if (!this.showFormError) return '';
    const errors: string[] = [];
    if (this.form.get('reportCity')?.errors)
      errors.push(this.translateService.instant('appointment-info.city'));
    if (this.form.get('reportDistrict')?.errors)
      errors.push(this.translateService.instant('appointment-info.district'));

    const addressErrors = this.form.get('reportAddress')?.errors;
    if (addressErrors) {
      if (addressErrors['required']) {
        errors.push(this.translateService.instant('appointment-info.detailed-address'));
      }
      if (addressErrors['maxlength']) {
        // 超過字數限制 → 直接回傳，不要再跟其他欄位組合
        return this.translateService.instant('appointment-info.detailed-address-maxlength');
      }
    }
    return errors.length
      ? this.translateService.instant('error.please-fill-in', {
          fieldName: errors.join('、'),
        })
      : '';
  }

  //取得國外檢查報告地址錯誤訊息
  getForeignAddressErrorMessage(){
    if (!this.showFormError) return '';
    const errors: string[] = [];
    const addressErrors = this.form.get('foreignAddress')?.errors;
    if (addressErrors) {
      if (addressErrors['required']) {
        errors.push(this.translateService.instant('appointment-info.detailed-address'));
      }
      if (addressErrors['maxlength']) {
        // 超過字數限制 → 直接回傳，不要再跟其他欄位組合
        return this.translateService.instant('appointment-info.detailed-address-maxlength');
      }
    }
    return errors.length
      ? this.translateService.instant('error.please-fill-in', {
          fieldName: errors.join('、'),
        })
      : '';
  }

  // 取得餐食錯誤訊息
  getDietErrorMessage(): string {
    if (!this.showFormError) return '';
    const errors: string[] = [];
    if (this.form.get('breakfast')?.errors)
      errors.push(this.translateService.instant('appointment-info.breakfast'));
    if (this.form.get('lunch')?.errors)
      errors.push(this.translateService.instant('appointment-info.lunch'));
    return errors.length
      ? this.translateService.instant('error.please-select', {
          fieldName: errors.join('、'),
        })
      : '';
  }

  // 是否為個人預約
  get isPersonalReserve(): boolean {
    return (
      this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.PERSONAL
    );
  }

  // 提交表單
  onSubmit() {
    this.showFormError = true;
    
    if (this.form.valid) {
      const bookingInfo: BookingInfo = {
        checkupDate: {} as BookDatetimeInfo, // 健檢報到日，需從其他頁面獲取
        nutrition: undefined,
        imaging: undefined,
        imagingContact: undefined,
        orgNo: this.sharedStorageService.get('userPlanRequest')?.orgNo ?? '',
        preExamInfoDeliveryMethod: this.noticeSendType
          ? this.noticeSendType
          : API_PARAMS.PRE_EXAM_INFO_DELIVERY_METHOD.INDIVIDUAL.code,
        deliveryMethod: this.reportSendType === '1'
          ? (this.form.get('deliveryMethod')?.value ?? '')
          : this.reportSendType,
        city: this.form.get('city')?.value ?? '',
        district: this.form.get('district')?.value?.areaName ?? '',
        zipCode: this.form.get('district')?.value?.zipCode ?? '',
        address: this.form.get('address')?.value ?? '',
        reportCity: this.form.get('reportCity')?.value ?? '',
        reportDistrict: this.form.get('reportDistrict')?.value?.areaName ?? '',
        reportZipCode: this.form.get('reportDistrict')?.value?.zipCode ?? '',
        reportAddress: this.form.get('reportAddress')?.value ?? '',
        foreignAddress: this.form.get('foreignAddress')?.value ?? '',
        isChecked: this.isChecked,
        deliveryRecipient: this.form.get('deliveryRecipient')?.value || API_PARAMS.DELIVERY_METHOD.SELF.code,
        recipient: this.form.get('recipient')?.value ?? '',
        recipientCell: this.form.get('recipientCell')?.value ?? '',
        email: this.form.get('email')?.value ?? '',
        language: this.form.get('language')?.value ?? '',
        clothesSize: this.form.get('clothSize')?.value ?? '',
        breakfast: this.form.get('breakfast')?.value ?? '',
        lunch: this.form.get('lunch')?.value ?? '',
        dayType: '', // 全日/半日，從appointment-time頁面獲取
        noticeSendType: this.noticeSendType ?? '1',//限企業-檢前說明寄送方式
        reportSendType: this.reportSendType ?? '1',//限企業-檢後報告寄送方式
      };
      console.log('bookingInfo', bookingInfo);
      this.sharedStorageService.set('bookingInfo', bookingInfo);
      this.router.navigate(['/healthcheck/reserve/timeselect']).then();

      console.log('選到的 clothSize code:', this.form.get('clothSize')?.value);
      console.log('clothesSizes:', this.clothesSizes);
    } else {
      console.log("表單驗證失敗");
      this.scrollToFirstInvalidField();
    }
  }

  // function to scroll to the first invalid field
  scrollToFirstInvalidField() {
    const invalidControls = Object.keys(this.form.controls).filter(
      key => this.form.get(key)?.invalid
    );

    if (!invalidControls.length) return;

    const firstInvalid = invalidControls[0];

    // get the target element with the wanted data-control-name attribute
    const el = this.fieldRefs.find(ref => {
      const nativeEl = ref?.nativeElement;
      return nativeEl?.getAttribute?.('data-control-name') === firstInvalid;
    });

    if (el?.nativeElement) {
      el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.nativeElement.focus?.();
    }
  }

  // 新增方法：檢查報告地址是否與檢前資料完全相同
  private checkAndUpdateIsChecked(): void {
    const reportCity = this.form.get('reportCity')?.value;
    const reportDistrict = this.form.get('reportDistrict')?.value;
    const reportAddress = this.form.get('reportAddress')?.value;

    const preExamCity = this.form.get('city')?.value;
    const preExamDistrict = this.form.get('district')?.value;
    const preExamAddress = this.form.get('address')?.value;

    console.log('檢查地址是否相同:', {
      reportCity, reportDistrict, reportAddress,
      preExamCity, preExamDistrict, preExamAddress
    });

    // 檢查是否有任何欄位為空值
    const hasEmptyFields = !reportCity || !reportDistrict || !reportAddress || 
                         !preExamCity || !preExamDistrict || !preExamAddress;

    // 如果有空值,則不判斷為相同
    if (hasEmptyFields) {
      this.isChecked = false;
      this.updateBookingInfoIsChecked();
      return;
    }

    // 檢查三個欄位是否都相同
    const isSame = 
      reportCity === preExamCity &&
      reportDistrict?.areaName === preExamDistrict?.areaName &&
      reportDistrict?.zipCode === preExamDistrict?.zipCode &&
      reportAddress === preExamAddress;

    console.log('是否相同:', isSame);

    this.isChecked = isSame;
    
    // 更新 bookingInfo
    this.updateBookingInfoIsChecked();
  }

  private updateBookingInfoIsChecked(): void {
    const bookingInfo = this.sharedStorageService.get('bookingInfo');
    if (bookingInfo) {
      bookingInfo.isChecked = this.isChecked;
      this.sharedStorageService.set('bookingInfo', bookingInfo);
    }
  }
}
